import { CircularProgress } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AccountTrxTypeCategory } from '@/utils'

interface DataTableProps<T extends Record<string, unknown>> {
  headers: string[]
  columnMapping: Record<string, keyof T>
  rows: T[]
  selectedRowIndex: number | null
  handleAccountChecked: (isChecked: boolean, index: number) => void
  isCheckbox?: boolean
  status?: string
  onSort?: (key: string) => void
  sortConfig?: { key: string; direction: 'asc' | 'desc' } | null
  category?: string
}

const DataTable = <T extends Record<string, unknown>>({
  headers,
  columnMapping = {},
  rows = [],
  selectedRowIndex,
  handleAccountChecked,
  isCheckbox,
  status,
  onSort,
  sortConfig,
  category,
}: DataTableProps<T>) => {
  return (
    <>
      {status === 'loading' ? (
        <div className='w-full h-[10vh] m-0 flex justify-center items-center'>
          <CircularProgress className='text-pi-principal-blue' />
        </div>
      ) : (
        <>
          {rows.length > 0 ? (
            <div
              className='pt-4 w-full overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 overflow-y-auto border border-pi-gray-2'
              style={{ scrollbarWidth: 'thin' }}
            >
              <table className='min-w-[800px] w-full table-auto border-collapse text-left border border-pi-gray-3'>
                <thead>
                  <tr className='bg-[#007bff] text-white table-auto border-collapse'>
                    {isCheckbox && (
                      <th className='text-left py-2 text-[14px] p-[10px]'>
                        Select
                      </th>
                    )}
                    {headers.map((header) => {
                      const isSorted = sortConfig?.key === columnMapping[header]
                      const arrow = isSorted ? (
                        sortConfig.direction === 'asc' ? (
                          <ExpandLessIcon fontSize='small' />
                        ) : (
                          <ExpandMoreIcon fontSize='small' />
                        )
                      ) : (
                        ''
                      )
                      return (
                        <th
                          className={`text-left py-2 text-[14px] p-[10px] ${
                            category ===
                            AccountTrxTypeCategory.TRANSACTION_HISTORY
                              ? 'cursor-pointer'
                              : ''
                          }`}
                          key={header}
                          onClick={() => onSort?.(header)}
                        >
                          <span className='flex items-center gap-1'>
                            {header}
                            {arrow}
                          </span>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={index}
                      className={`pl-2 ${
                        index % 2 === 1 ? 'bg-pi-gray-4' : ''
                      }`}
                    >
                      {isCheckbox && (
                        <td className='text-center py-2 border '>
                          <input
                            type='checkbox'
                            className='form-checkbox h-4 w-4'
                            checked={selectedRowIndex === index}
                            onChange={(e) =>
                              handleAccountChecked(e.target.checked, index)
                            }
                          />
                        </td>
                      )}
                      {Object.keys(columnMapping).map((key, idx) => {
                        let cellValue = row[
                          columnMapping[key] as keyof T
                        ] as unknown

                        if (
                          key === 'Fund Name' &&
                          (cellValue === undefined || cellValue === null)
                        ) {
                          cellValue = row['fundCode' as keyof T]
                        }
                        if (
                          key === 'Sub Acc A (RM)' ||
                          key === 'Sub Acc B (RM)'
                        ) {
                          const subKey =
                            key === 'Sub Acc A (RM)' ? 'subAccA' : 'subAccB'

                          if (subKey in row) {
                            const value = (
                              row as Record<'subAccA' | 'subAccB', unknown>
                            )[subKey]
                            if (
                              typeof value === 'number' ||
                              typeof value === 'string'
                            ) {
                              cellValue = Number(cellValue).toFixed(2)
                            }
                          }
                        }

                        if (
                          (key === 'Application Date' ||
                            key === 'Transaction Date' ||
                            key === 'RWP Start Date' ||
                            key === 'RWP End Date' ||
                            key === 'NAV Per Unit as at') &&
                          cellValue
                        ) {
                          if (
                            typeof cellValue === 'string' ||
                            typeof cellValue === 'number'
                          ) {
                            const dateObj = new Date(cellValue)
                            const year = dateObj.getFullYear()
                            const month = String(
                              dateObj.getMonth() + 1
                            ).padStart(2, '0')
                            const day = String(dateObj.getDate()).padStart(
                              2,
                              '0'
                            )
                            cellValue = `${day}/${month}/${year}`
                          }
                        }

                        if (
                          key === 'Current Holding Value(RM)' ||
                          key === 'Gross Amount (RM)' ||
                          key === 'Total Transfer Out (RM)' ||
                          key === 'Transfer Out (RM)' ||
                          key === 'Total Transfer In (RM)' ||
                          key === 'Sales Charges (RM)' ||
                          key === 'SST Amount (RM)' ||
                          key === 'PPA Opening Fee (RM)' ||
                          key === 'PPA Annual Fee (RM)' ||
                          key === 'Total Contribution (RM)' ||
                          key === 'Total Contribution + Dividend (RM)' ||
                          key === 'Sub Acc B (RM)' ||
                          key === 'Sub Acc A (RM)' ||
                          key === 'Total Switch In (RM)' ||
                          key === 'Total Switch Out (RM)' ||
                          key === 'Total Withdrawal (RM)' ||
                          key === 'Current Holding Units' ||
                          key === 'Current Holding Value (RM)' ||
                          key === 'PPA Fee (RM)' ||
                          key === 'Charges' ||
                          key === 'Fees (RM)' ||
                          key === 'Tax Penalty (RM)' ||
                          key === 'Net Amount (RM)' ||
                          key === 'Units' ||
                          key === 'Withdrawal Penalty Tax (RM)' ||
                          (key === 'Amount' && cellValue)
                        ) {
                          const keyValue = Number(cellValue)
                          cellValue = new Intl.NumberFormat('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(keyValue)
                        }
                        if (key === 'NAV Per Unit (RM)' && cellValue) {
                          const keyValue = Number(cellValue)
                          cellValue = new Intl.NumberFormat('en-IN', {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          }).format(keyValue)
                        }

                        return (
                          <td
                            key={idx}
                            className='text-left py-2 text-[14px] p-[10px]'
                          >
                            {String(cellValue ?? '')}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='pt-4'>
              <span className='h-[10vh]'>No item found</span>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default DataTable
